import type {
    ContractStatusEnum,
    WorkOrderPriceItem,
    WorkOrderStatusEnum,
    WorkOrderVersionActKc2VersionData,
} from 'gql/graphql';
import { ActKc2StatusEnum, DeviationEnum } from 'gql/graphql';
import { formatActTitle } from 'pages/Project/Common/ActKc2Title/ActKc2Title';
import { DeviatedColumnTitle } from 'utils/tableCells';

import type { ColDef, ColGroupDef } from 'ag-grid-community';
import { useMemo } from 'react';

import { TitleActKC2Columns } from '../../WorkOrder/WorkOrderPrice/TitleActKC2Columns';
import { ActCostColType, ActCostRenderer, ActVolumeRenderer } from '../../WorkOrder/WorkOrderTable/cellRenderers';

interface IUseActKC2ColumnsProps {
    workOrderActs: WorkOrderVersionActKc2VersionData[] | null | undefined;
    contractStatus: ContractStatusEnum | undefined;
    workOrderStatus: WorkOrderStatusEnum | undefined;
    isDisabledByProjectStatus: boolean;
    costRounding: number;
}

export const useWorkOrderVersionActsColumns = ({
    workOrderActs,
    contractStatus,
    workOrderStatus,
    isDisabledByProjectStatus,
    costRounding,
}: IUseActKC2ColumnsProps) =>
    useMemo<(ColDef<WorkOrderPriceItem> | ColGroupDef<WorkOrderPriceItem>)[]>(() => {
        let isOddSeason = true;
        const currentSeasonId = workOrderActs?.[0]?.seasonId;

        return (workOrderActs ?? []).map(
            ({ id, seasonId, status, number, date, fullNumber, isInactive, isCorrection, correctionDate }) => {
                if (seasonId !== currentSeasonId) {
                    isOddSeason = !isOddSeason;
                }

                const volumeColPlan: (ColDef<WorkOrderPriceItem> | ColGroupDef<WorkOrderPriceItem>)[] = [
                    {
                        // eslint-disable-next-line react/display-name
                        headerComponent: () => (
                            <div style={{ display: 'flex', width: '100%' }}>
                                <DeviatedColumnTitle
                                    title="Объем (План)"
                                    deviation={DeviationEnum.None}
                                    tooltipText="Превышает остаток по сезону"
                                />
                            </div>
                        ),
                        width: 155,
                        minWidth: 155,
                        cellRenderer: ActVolumeRenderer,
                        cellRendererParams: {
                            seasonId,
                            workOrderStatus,
                            actId: id,
                            deviationKey: 'actVolumePlanDeviation',
                            valueKey: 'actVolumePlan',
                            contractStatus,
                            isDisabledByProjectStatus,
                        },
                        headerClass: `ag-right-aligned-header`,
                        cellClass: `ag-right-aligned-cell ${isInactive ? 'inactive-act' : undefined}`,
                    },
                ];

                const costColPlan: (ColDef<WorkOrderPriceItem> | ColGroupDef<WorkOrderPriceItem>)[] = [
                    {
                        field: `actKC2PlanCost_${id}` as any,
                        headerName: 'Стоимость (План)',
                        width: 150,
                        cellRenderer: ActCostRenderer,
                        cellRendererParams: {
                            seasonId,
                            workOrderStatus,
                            actId: id,
                            valueKey: 'actCostPlan',
                            deviationKey: 'actCostPlanDeviation',
                            contractStatus,
                            isDisabledByProjectStatus,
                            decimal: costRounding,
                            colType: ActCostColType.planCost,
                        },
                        cellClass: `ag-right-aligned-cell ${isInactive ? 'inactive-act' : undefined}`,
                        suppressKeyboardEvent: () => true,
                    },
                ];

                return {
                    seasonId,
                    id,
                    // eslint-disable-next-line react/display-name
                    headerGroupComponent: () => (
                        <TitleActKC2Columns
                            id={id}
                            number={number}
                            status={status}
                            date={date}
                            contractStatus={contractStatus}
                            workOrderStatus={workOrderStatus}
                            isDisabledByProjectStatus={isDisabledByProjectStatus}
                            chapter={undefined}
                            workOrderSubObjectId={undefined}
                            seasonId={seasonId}
                            fullNumber={fullNumber}
                            isInactive={isInactive}
                            isCorrection={isCorrection}
                            correctionDate={correctionDate}
                        />
                    ),
                    headerName: formatActTitle(
                        isCorrection ? fullNumber : number,
                        isCorrection ? correctionDate : date,
                    ),
                    marryChildren: true,
                    children: [
                        ...(status !== ActKc2StatusEnum.Accepted ? volumeColPlan : []),
                        ...(status !== ActKc2StatusEnum.Accepted ? costColPlan : []),
                        {
                            colId: id,
                            // eslint-disable-next-line react/display-name
                            headerComponent: () => (
                                <DeviatedColumnTitle
                                    title="Объем (Согласовано)"
                                    deviation={DeviationEnum.None}
                                    tooltipText="Превышает остаток по сезону"
                                />
                            ),
                            width: 155,
                            headerClass: `ag-right-aligned-header `,
                            cellClass: `ag-right-aligned-cell ${isInactive ? 'inactive-act' : undefined}`,
                            cellRenderer: ActVolumeRenderer,
                            cellRendererParams: {
                                seasonId,
                                workOrderStatus,
                                actId: id,
                                deviationKey: 'actVolumeAcceptedDeviation',
                                valueKey: 'actVolumeAccepted',
                                contractStatus,
                                isDisabledByProjectStatus,
                            },
                        },
                        {
                            field: `actKC2_${id}_cost_accepted`,
                            headerName: 'Стоимость (Согласовано)',
                            width: 155,
                            cellRenderer: ActCostRenderer,
                            cellRendererParams: {
                                seasonId,
                                workOrderStatus,
                                actId: id,
                                valueKey: 'actCostAccepted',
                                deviationKey: 'actCostAcceptedDeviation',
                                contractStatus,
                                isDisabledByProjectStatus,
                                decimal: costRounding,
                                colType: ActCostColType.acceptedCost,
                            },
                            cellClass: `ag-right-aligned-cell ${isInactive ? 'inactive-act' : undefined}`,
                        },
                    ],
                };
            },
        );
    }, [contractStatus, costRounding, isDisabledByProjectStatus, workOrderActs, workOrderStatus]);
